import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { FacebookSVG } from 'images/latest'
import { Button } from 'ui/bend'
import PropTypes from 'prop-types'
import { refer_a_friend_event } from 'gtm/pushEvents'

/* Modal: unpadded shell so `.close` matches signup `.signupHeader .close` (absolute to header edges, not inside padded body). */
const ModalPanel = styled.div`
  position: relative;
  max-width: 480px;
  margin: 0 auto;
  background: #fff;
  border-radius: 4px;
`

const ModalBody = styled.div`
  padding: 32px;

  @media (max-width: 600px) {
    padding: 24px 16px;
  }
`

const Container = styled.div`
  max-width: 480px;
  margin: 80px auto;
  padding: 32px;
  background: #fff;
  border: 1px solid #eaeaea;
  border-radius: 4px;

  @media (max-width: 600px) {
    margin: 24px auto;
    padding: 24px 16px;
    border: none;
  }
`

const JoinNow = styled.div`
  text-align: center;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 135%;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 0 10px 10px 10px;
  border-bottom: 4px solid #000;
  margin-bottom: 24px;
`

const SocialGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  width: 100%;

  @media (max-width: 600px) {
    gap: 8px;
  }
`

const ActionButton = styled(Button)`
  font-family: 'Proxima-Nova';
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  flex: 1 1 calc(50% - 12px);
  padding: 12px !important;
  background: #fff !important;
  color: #000 !important;
  border: 1px solid #000 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  @media (max-width: 600px) {
    flex: 1 1 calc(50% - 8px);
    padding: 10px !important;
    font-size: 12px !important;
  }

  &:hover {
    background: #000 !important;
    color: #fff !important;
  }

  svg {
    width: 16px !important;
    height: 16px !important;
    margin-right: 8px !important;
  }

  &:hover svg.fb-icon {
    filter: invert(1);
  }
`

const CopyButton = styled(Button)`
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-weight: 500;
  text-transform: uppercase;
  width: 100%;
  padding: 12px 20px !important;
  color: #fff;
  background: #000;
  margin-top: 16px;
  border: 1px solid #000 !important;

  &:hover {
    background: #333;
    border-color: #333 !important;
  }
`

const InputBox = styled.input`
  width: 100%;
  font-family: 'Proxima-Nova';
  font-size: 14px;
  padding: 12px;
  margin-top: 8px;
  border: 1px solid #ccc;
  border-radius: 2px;
  background: #f9f9f9;
  color: #333;
  outline: none;
`

const LinkIcon = () => (
  <svg viewBox='0 0 24 24'>
    <path d='M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z' />
  </svg>
)

const ChatIcon = () => (
  <svg viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M35 25C35 25.8841 34.6488 26.7319 34.0237 27.357C33.3986 27.9821 32.5507 28.3333 31.6667 28.3333H11.6667L5 35V8.33333C5 7.44928 5.35119 6.60143 5.97631 5.97631C6.60143 5.35119 7.44928 5 8.33333 5H31.6667C32.5507 5 33.3986 5.35119 34.0237 5.97631C34.6488 6.60143 35 7.44928 35 8.33333V25Z'
      stroke='currentColor'
      strokeWidth='2.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const EmailIcon = () => (
  <svg viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M6.66634 6.66669H33.333C35.1663 6.66669 36.6663 8.16669 36.6663 10V30C36.6663 31.8334 35.1663 33.3334 33.333 33.3334H6.66634C4.83301 33.3334 3.33301 31.8334 3.33301 30V10C3.33301 8.16669 4.83301 6.66669 6.66634 6.66669Z'
      stroke='currentColor'
      strokeWidth='2.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M36.6663 10L19.9997 21.6667L3.33301 10'
      stroke='currentColor'
      strokeWidth='2.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const MoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"></circle>
    <circle cx="6" cy="12" r="3"></circle>
    <circle cx="18" cy="19" r="3"></circle>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
  </svg>
)

const ReferralShare = ({
  userId,
  isModal,
  onClose,
  entryPoint,
  shareSurface
}) => {
  const [copied, setCopied] = useState(false)
  const [canShare, setCanShare] = useState(false)
  
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      setCanShare(true)
    }
  }, [])
  
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://www.alomoves.com'
  const shareUrl = `${baseUrl}/invite/${userId}`
  const shareText =
    "Wellness is better together. Join me on ALO Wellness Club—it's free to start:"

  const trackShare = (destination) => {
    refer_a_friend_event({
      interaction: 'share',
      entry_point: entryPoint,
      share_surface: shareSurface,
      destination
    })
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${shareUrl}`)
    trackShare('copy_link')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleEmail = () => {
    trackShare('email')
    const url = `${shareUrl}?m=email`
    const subject = encodeURIComponent("You're invited to ALO Wellness Club!")
    const body = encodeURIComponent(`${shareText} ${url}`)
    window.location.href = `mailto:?subject=${subject}&body=${body}`
  }

  const handleSms = () => {
    trackShare('message')
    const url = `${shareUrl}?m=sms`
    window.location.href = `sms:?&body=${encodeURIComponent(shareText + ' ' + url)}`
  }

  const handleSocial = () => {
    trackShare('facebook')
    const url = `${shareUrl}?m=social_share`
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    window.open(fbUrl, 'facebook-share-dialog', 'width=626,height=436,noopener,noreferrer')
  }

  const handleMore = () => {
    if (navigator.share) {
      trackShare('more_share')
      navigator.share({
        title: "ALO Wellness Club",
        text: shareText,
        url: `${shareUrl}?m=native_share`
      }).catch((error) => console.log('Error sharing', error))
    }
  }

  const shareBody = (
    <>
      <JoinNow>REFER A FRIEND</JoinNow>

      <div style={{ marginBottom: '24px', textAlign: 'center', fontFamily: 'Proxima-Nova', fontSize: '14px', lineHeight: '1.5' }}>
        Wellness is better together. Invite your friends to experience ALO Wellness Club for free using your link below.
      </div>

      <InputBox
        type='text'
        readOnly
        value={shareUrl}
        onClick={(e) => e.target.select()}
      />

      <CopyButton onClick={handleCopyLink} id="copy_link_button">
        {copied ? 'COPIED!' : 'COPY INVITE LINK'}
      </CopyButton>

      <SocialGrid>
        <ActionButton onClick={handleEmail}>
          <EmailIcon />
          <span>Email</span>
        </ActionButton>

        <ActionButton onClick={handleSms}>
          <ChatIcon />
          <span>SMS</span>
        </ActionButton>

        <ActionButton onClick={handleSocial}>
          <FacebookSVG className="fb-icon" width='16' height='16' style={{ marginRight: '8px' }} />
          <span>Facebook</span>
        </ActionButton>

        {canShare && (
          <ActionButton onClick={handleMore}>
            <MoreIcon />
            <span>More</span>
          </ActionButton>
        )}
      </SocialGrid>
    </>
  )

  if (isModal) {
    return (
      <ModalPanel className='referral-share-modal__panel'>
        {onClose && (
          <button
            type='button'
            className='close'
            onClick={onClose}
            aria-label='Close'
          >
            <span aria-hidden='true'>&times;</span>
          </button>
        )}
        <ModalBody>{shareBody}</ModalBody>
      </ModalPanel>
    )
  }

  return <Container>{shareBody}</Container>
}

ReferralShare.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isModal: PropTypes.bool,
  onClose: PropTypes.func,
  /** GTM `entry_point` — set by modal parent or default for /referrals/share */
  entryPoint: PropTypes.string,
  /** GTM `share_surface`: modal vs dedicated page */
  shareSurface: PropTypes.oneOf(['modal', 'page'])
}

ReferralShare.defaultProps = {
  isModal: false,
  onClose: undefined,
  entryPoint: 'referral_page',
  shareSurface: 'page'
}

export default ReferralShare
